import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';

// YouTube's iframe API, loaded once, when the first video opens
let youtubeApi;
const loadYoutubeApi = () =>
  (youtubeApi ||= new Promise((resolve) => {
    if (window.YT?.Player) {
      resolve(window.YT);
      return;
    }
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previous?.();
      resolve(window.YT);
    };
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;
    document.head.appendChild(script);
  }));

const SAVE_EVERY_MS = 15000;
const PLAYING = 1;
const PAUSED = 2;
const ENDED = 0;

/**
 * A Central de Aprendizado video in YouTube's player (youtube-nocookie), from where the member stopped.
 * onProgress(position, duration) runs every 15 s while it plays, on pause and on leaving; onEnded when it finishes.
 * Channels that turned off playing outside YouTube get a link to watch it there.
 */
export const AcademyPlayer = ({ video, autoplay = false, startAt = 0, onProgress, onEnded }) => {
  const { t } = useTranslation();
  const boxRef = useRef(null);
  const [isBlocked, setIsBlocked] = useState(false);
  // The latest callbacks, without remounting the player when they change
  const callbacks = useRef({ onProgress, onEnded });
  callbacks.current = { onProgress, onEnded };

  useEffect(() => {
    setIsBlocked(false);
    let player;
    let timer;
    let isGone = false;
    let lastReport = 0;

    const report = (force) => {
      try {
        const duration = player?.getDuration?.();
        const position = player?.getCurrentTime?.();
        if (!duration || (!force && Date.now() - lastReport < SAVE_EVERY_MS)) return;
        lastReport = Date.now();
        callbacks.current.onProgress?.(Math.round(position), Math.round(duration));
      } catch {
        // The player is already gone
      }
    };

    const mount = document.createElement('div');
    boxRef.current?.appendChild(mount);

    loadYoutubeApi().then((YT) => {
      if (isGone) return;
      player = new YT.Player(mount, {
        videoId: video.youtubeId,
        host: 'https://www.youtube-nocookie.com',
        width: '100%',
        height: '100%',
        playerVars: { autoplay: autoplay ? 1 : 0, rel: 0, playsinline: 1, start: Math.max(0, Math.floor(startAt)) },
        events: {
          onStateChange: (event) => {
            if (event.data === PAUSED) report(true);
            if (event.data === ENDED) callbacks.current.onEnded?.();
          },
          onError: (event) => {
            if (event.data === 101 || event.data === 150) setIsBlocked(true);
          },
        },
      });
      timer = setInterval(() => {
        if (player?.getPlayerState?.() === PLAYING) report(false);
      }, 5000);
    });

    return () => {
      isGone = true;
      clearInterval(timer);
      // Leaving mid-video keeps the spot
      if ([PLAYING, PAUSED].includes(player?.getPlayerState?.())) report(true);
      try {
        player?.destroy();
      } catch {
        // Already torn down
      }
      mount.remove();
    };
    // A new video is a new player; the start and autoplay of the one playing don't restart it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video.youtubeId]);

  return (
    <div className="mt-ac-player" style={{ backgroundImage: `url(https://i.ytimg.com/vi/${video.youtubeId}/hqdefault.jpg)` }}>
      <div ref={boxRef} className="mt-ac-player__frame" title={video.title} />
      {isBlocked && (
        <div className="mt-ac-player__blocked">
          <b>{t('pages.Academy.ui.embedBlocked')}</b>
          <a href={`https://www.youtube.com/watch?v=${video.youtubeId}`} target="_blank" rel="noopener noreferrer">
            {t('pages.Academy.ui.watchOnYoutube')}
            <ExternalLink aria-hidden="true" />
          </a>
        </div>
      )}
    </div>
  );
};
